/**
 * Hook for managing hotel feature configuration
 * Handles GET/PUT operations with loading and error states
 */

import { useState, useEffect, useCallback } from 'react';
import { featureSettingsApi } from '../api/featureSettingsApi';
import { toast } from 'sonner';

export function useHotelFeatures(hotelId) {
  const [config, setConfig] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Fetch feature configuration
  const loadFeatures = useCallback(async () => {
    if (!hotelId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const [featuresData, metadataData] = await Promise.all([
        featureSettingsApi.getFeatures(hotelId),
        featureSettingsApi.getMetadata()
      ]);
      
      setConfig(featuresData.config);
      setPricing(featuresData.pricing);
      setValidationErrors(featuresData.validation_errors || {});
      setMetadata(metadataData.features);
    } catch (err) {
      setError(err.message);
      toast.error('Failed to load feature settings');
    } finally {
      setLoading(false);
    }
  }, [hotelId]);

  // Update feature configuration
  const saveFeatures = useCallback(async (updatedConfig) => {
    try {
      setSaving(true);
      setError(null);
      
      const result = await featureSettingsApi.updateFeatures(hotelId, updatedConfig);
      
      setConfig(result.config);
      setPricing(result.pricing);
      setValidationErrors({});
      
      toast.success('Feature settings saved successfully');
      return result;
    } catch (err) {
      setError(err.message);
      toast.error(err.message || 'Failed to save feature settings');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [hotelId]);

  // Update a single feature toggle
  const toggleFeature = useCallback((featureKey, value) => {
    setConfig(prev => ({
      ...prev,
      [featureKey]: value
    }));
  }, []);

  // Load on mount
  useEffect(() => {
    loadFeatures();
  }, [loadFeatures]);

  return {
    config,
    pricing,
    validationErrors,
    metadata,
    loading,
    saving,
    error,
    toggleFeature,
    saveFeatures,
    reload: loadFeatures
  };
}
